<template>
  <div class="date-range-filter position-relative">
    <custom-date
      v-model="selectedDateValue"
      :newDays="days"
      :disabled="disabled"
      :border="false"
      :custom-label="customRangeLabel"
      @date-changed="onDateChanged"
      @show-custom-range="onCustomRangeChange"
    />

    <!-- Custom date range picker - shown when custom is selected -->
    <div
      v-if="selectedDateValue === 'customDate'"
      class="custom-date-range-wrapper"
      :class="snapClass"
      @click.capture="scheduleAutoSnap"
    >
      <div class="d-flex align-items-center justify-content-between gap-2 bg-white">
        <date-range-picker
          ref="rangePicker"
          :start="startDate"
          :end="endDate"
          :locale="$i18n.locale"
          :initial-show="showCustomRange"
          v-bind="lastDate ? { lastDate } : {}"
          @selected="dateSelect"
          @cancel="handleCancel"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { format, isValid, lastDayOfMonth, parse, parseISO } from 'date-fns';
import DateRangePicker from '@vektor/date-picker';
import { defaults } from '@/helpers/appDefaults';
import { getDateRangeByType } from '@/helpers/dateTime';
import customDate from './date.vue';

export default {
  name: 'DateRangeFilter',
  components: {
    DateRangePicker,
    customDate,
  },
  props: {
    snap: {
      type: String,
      default: 'auto',
      validator: (value) => ['left', 'right', 'auto'].includes(value),
    },
    defaultSelection: {
      type: String,
      default: 'yesterday',
    },
    selectedDateType: {
      type: String,
      default: null,
    },
    selectedDate: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    lastDate: {
      type: Date,
      default: null,
    },
    fromDate: {
      type: String,
      default: null,
    },
    toDate: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      selectedDateValue: null,
      lastSelectedDate: null,
      selectedRange: {
        fromDate: null,
        toDate: null,
      },
      days: [
        'today',
        'yesterday',
        'thisWeek',
        'lastSevenDays',
        'thisMonth',
        'lastThirtyDays',
        'lastMonth',
        'customDate',
      ],
      showCustomRange: false,
      resolvedSnap: 'right',
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      endDate: lastDayOfMonth(new Date()),
    };
  },
  computed: {
    snapClass() {
      const direction = this.snap === 'auto' ? this.resolvedSnap : this.snap;
      return `align-${direction}`;
    },
    customRangeLabel() {
      // Display formatted range when a custom range is active and dates are available
      if (this.selectedDateValue !== 'customDate') return null;
      const from = this.selectedRange.fromDate || this.fromDate;
      const to = this.selectedRange.toDate || this.toDate;
      if (!from || !to) return null;
      return `${this.formatRangeLabelDate(from)} - ${this.formatRangeLabelDate(to)}`;
    },
  },
  watch: {
    selectedDate: {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (!newValue) return;
        const { type, fromDate, toDate } = newValue;
        // Don't override the selection while the custom range picker is open —
        // otherwise a periodic refresh that re-renders the parent would reset
        // selectedDateValue and close the picker.
        if (this.showCustomRange) return;
        if (type && type !== this.selectedDateValue) {
          this.selectedDateValue = type;
          this.lastSelectedDate = type;
        }
        if (type === 'customDate') {
          this.selectedRange = {
            fromDate: fromDate || null,
            toDate: toDate || null,
          };
          // Parse and set Date objects for the date picker when dates come from URL
          if (fromDate && toDate) {
            const parsedStart = this.parseRangeDate(fromDate);
            const parsedEnd = this.parseRangeDate(toDate);
            if (parsedStart && parsedEnd) {
              this.startDate = parsedStart;
              this.endDate = parsedEnd;
            }
          }
        } else {
          this.selectedRange = { fromDate: null, toDate: null };
        }
      },
    },
    selectedDateType(newValue) {
      if (newValue && newValue !== this.selectedDateValue) {
        this.selectedDateValue = newValue;
        this.lastSelectedDate = newValue;
      }
      if (newValue !== 'customDate') {
        this.selectedRange = {
          fromDate: null,
          toDate: null,
        };
      }
    },
    fromDate(newValue) {
      if (this.selectedRange.fromDate && newValue && newValue !== this.selectedRange.fromDate) {
        this.selectedRange = {
          ...this.selectedRange,
          fromDate: null,
        };
      }
    },
    toDate(newValue) {
      if (this.selectedRange.toDate && newValue && newValue !== this.selectedRange.toDate) {
        this.selectedRange = {
          ...this.selectedRange,
          toDate: null,
        };
      }
    },
  },
  created() {
    const initialSelectedDate = this.selectedDate?.type || this.selectedDateType || this.defaultSelection;
    this.selectedDateValue = initialSelectedDate;
    this.lastSelectedDate = initialSelectedDate;
  },
  mounted() {
    window.addEventListener('resize', this.onViewportResize);
    if (this.selectedDateValue === 'customDate') this.scheduleAutoSnap();
    if (this.selectedDateValue === 'today') {
      this.$emit('change', getDateRangeByType('today'));
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onViewportResize);
  },
  methods: {
    scheduleAutoSnap() {
      if (this.snap !== 'auto') return;
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.resolvedSnap = this.calculateSnap();
        });
      });
    },
    onViewportResize() {
      if (this.snap === 'auto' && this.selectedDateValue === 'customDate') this.resolvedSnap = this.calculateSnap();
    },
    onDateChanged(event) {
      this.$emit('change', getDateRangeByType(this.selectedDateValue, event));
    },
    onCustomRangeChange(data) {
      if (data === true) {
        const fromDate = this.selectedRange.fromDate || this.fromDate;
        const toDate = this.selectedRange.toDate || this.toDate;
        const startDate = this.parseRangeDate(fromDate);
        const endDate = this.parseRangeDate(toDate);
        if (startDate && endDate) {
          this.startDate = startDate;
          this.endDate = endDate;
        } else {
          const today = new Date();
          this.startDate = today;
          this.endDate = today;
        }
        this.lastSelectedDate = this.selectedDateValue;
        this.showCustomRange = true;
        // Fallback: ensure the picker overlay is visible even if initial-show is missed
        this.$nextTick(() => {
          const picker = this.$refs.rangePicker;
          if (picker && !picker.isOpen && typeof picker.showOverlay === 'function') {
            picker.showOverlay();
          }
          this.scheduleAutoSnap();
        });
      } else {
        this.closeCustomRangeOverlay();
      }
    },
    parseRangeDate(value) {
      if (!value) return null;
      if (value instanceof Date) return isValid(value) ? value : null;
      const parsedDefaultDate = parse(value, defaults.date, new Date());
      if (isValid(parsedDefaultDate)) return parsedDefaultDate;
      const parsedIsoDate = parseISO(value);
      return isValid(parsedIsoDate) ? parsedIsoDate : null;
    },
    formatRangeLabelDate(value) {
      const parsedDate = this.parseRangeDate(value);
      return parsedDate ? format(parsedDate, 'dd/MM/yy') : value;
    },
    calculateSnap() {
      const triggerRect = this.$el.getBoundingClientRect();
      const contentArea = this.$el.closest('.content-area');
      const contentRect = contentArea?.getBoundingClientRect();
      const leftBoundary = Math.max(0, contentRect?.left || 0);
      const rightBoundary = Math.min(window.innerWidth, contentRect?.right || window.innerWidth);
      const spaceRight = rightBoundary - triggerRect.left;
      const spaceLeft = triggerRect.right - leftBoundary;
      const calendar = this.$el.querySelector('.v-date-range-calendar-wrapper');
      const calendarWidth = calendar?.getBoundingClientRect().width || calendar?.scrollWidth || 0;

      if (calendarWidth && spaceRight >= calendarWidth) return 'left';
      if (calendarWidth && spaceLeft >= calendarWidth) return 'right';
      return spaceRight >= spaceLeft ? 'left' : 'right';
    },
    closeCustomRange() {
      this.showCustomRange = false;
      this.selectedDateValue = this.lastSelectedDate;
    },
    closeCustomRangeOverlay() {
      this.showCustomRange = false;
      const picker = this.$refs.rangePicker;
      if (picker && typeof picker.hideOverlay === 'function') {
        picker.hideOverlay();
      }
    },
    handleCancel() {
      this.closeCustomRangeOverlay();
      this.selectedDateValue = this.lastSelectedDate;
    },
    dateSelect(event) {
      this.startDate = event.raw.startDate;
      this.endDate = event.raw.endDate;
      const fromDate = format(this.startDate, defaults.date);
      const toDate = format(this.endDate, defaults.date);
      // Track locally so the label reflects the new range immediately
      this.selectedRange = {
        fromDate,
        toDate,
      };
      // Close the picker wrapper once the user confirms the range
      this.closeCustomRangeOverlay();
      this.$emit('change', getDateRangeByType('customDate', { fromDate, toDate }));
    },
  },
};
</script>

<style scoped lang="scss">
// Component root styles
.date-range-filter {
  min-width: 210px;
  width: auto !important;

  ::v-deep .dropdown {
    width: 100%;
    height: 100%;
  }

  ::v-deep .control {
    font-size: 13px;
  }

  ::v-deep .arrow {
    width: 10px;
    font-size: 1.3em;
    line-height: 1.2;
    margin: 0.4em 2px;
    color: #5767ff;
  }

  ::v-deep .btn {
    width: 100%;
    text-align: left;
    line-height: inherit;
    height: 100%;
  }

  ::v-deep .dropdown-toggle {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  :deep(.btn:active),
  :deep(.btn.show),
  :deep(.btn:hover) {
    background-color: #fff;
  }
}

// Custom date range wrapper
.custom-date-range-wrapper {
  position: absolute;
  background: transparent;
  top: 0;
  z-index: 101;
  margin: 2px;
  bottom: 0;

  & > div {
    border-radius: 3px;
    height: 100%;
  }

  &.align-left {
    ::v-deep .v-date-range-calendar-wrapper {
      left: 0 !important;
      right: auto !important;
    }
  }

  &.align-right {
    ::v-deep .v-date-range-calendar-wrapper {
      left: auto !important;
      right: 0 !important;
    }
  }
}

.show-custom-range {
  z-index: 0;
}

// Date range picker overrides
::v-deep .v-date-range-picker {
  border: none !important;
  box-shadow: none !important;
  .selected-range:hover {
    cursor: pointer;
    color: #5767ff;
    background-color: #f8f9fa;
    border-radius: 5px;
  }
  // Override the global ::after pseudo-element from reportsHome.vue
  &::after {
    display: none !important;
  }

  .footer {
    font-size: 1em;
    position: absolute;
    left: 0;
    bottom: 0;
    border: none;
    padding: 0 1em;
    color: #5767ff;
  }
}

// Date range picker content
::v-deep .v-date-range-picker-content {
  .ranges {
    border: none;
    background-color: var(--bs-gray-100);

    ul li {
      padding: 2px 10px;
      margin: 5px;
      font-size: 12px;
      text-align: left;
      background-color: transparent;
      border-radius: 4px;
      cursor: pointer;
      color: #565656;
    }

    .button.submit {
      color: #fff;
      background-color: var(--bs-primary);
      border: none;
      padding: 2px 10px;
    }
  }

  .first,
  .second {
    padding-bottom: 20px;
  }
}

// Calendar styles
::v-deep .v-date-calendar {
  .date-header {
    margin-bottom: 1em;
  }

  .days-of-week {
    border: none;
    font-size: 12px;
    text-align: center;
    color: #5767ff;
    padding: 1em 0 0.5em;
  }

  .calendar-row {
    span.today {
      border: 1px solid #5767ff;
      border-radius: 5px;
      text-decoration: none;
      &.in-range {
        border: none;
        border-radius: 0;
      }
    }

    span.isEnd,
    span.isStart,
    span.in-range {
      background-color: #5767ff;
      text-decoration: none;
      color: #fff;
    }

    span.disabled {
      background-color: transparent;
      opacity: 0.5;
    }
  }
}

// Select dropdown arrow
::v-deep .select:not(.is-multiple):not(.is-loading)::after {
  border-color: var(--bs-secondary);
}
</style>
